import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Dialog, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import "@wooglobe/vre-editor/style.css";
import { VideoSource } from "../../Enums/enums";
import { createEditorServices } from "../../components/editorServices";
import { savedTemplateIdOf, useEditorUserKey } from "../../components/vreEditSession";
import { useBrandsTemplates } from "../hooks/useBrandTemplates";
import useBatchSession, { BatchStatus, isDoneStatus, needsCategory } from "./useBatchSession";
import { createBatchCaches } from "./batchCaches";
import { rawIdsOf } from "./batchItems";
import useTabRemoval from "./useTabRemoval";
import useVideoChoice from "./useVideoChoice";
import useApplyToAll from "./useApplyToAll";
import BatchEditorHeader from "./BatchEditorHeader";
import PlatformTabBar from "./PlatformTabBar";
import VideoTabBar from "./VideoTabBar";
import BatchEditorPane from "./BatchEditorPane";
import DuplicatePane from "./DuplicatePane";
import BatchConfirmDialog, { NO_CONFIRM } from "./BatchConfirmDialog";

const outlineSx = { borderRadius: 1.5, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "text.secondary", minHeight: 30, py: 0.25, fontSize: 13 };
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");
const SAVE_SHORTCUT = IS_MAC ? "⌘S" : "Ctrl+S";

/**
 * One editor for every brand × video × platform card set to Edited: videos as
 * tabs along the bottom, the selected video's platforms along the top. Only
 * the active tab has an editor and a video loaded.
 *
 * Nothing saves on its own: Save (or Ctrl/⌘+S) writes the active tab's edit to
 * its card's renderPayload, and leaving a tab with unsaved edits always asks.
 */
export default function BatchEditorDialog({ open, onClose, groups, onConfigChange, onRemoveVideo }) {
  const session = useBatchSession(open, groups);
  const { items, activeItem, activeVideo, activeIndex, savedCount, appliedCount, categoryMissingCount, statusOf, setMark, markUnsaved } = session;
  const total = items.length;

  const [chosen, setChosen] = useState({}); // tab key -> template picked in the gallery
  const [confirm, setConfirm] = useState(NO_CONFIRM);
  const [offer, setOffer] = useState(null); // { template, keys, brandName, platform }
  const [lastSave, setLastSave] = useState(null); // { key, ok, text }
  const paneRef = useRef(null);
  // The platform row's two ends, which the open tab's pane fills.
  const [leadingSlot, setLeadingSlot] = useState(null);
  const [trailingSlot, setTrailingSlot] = useState(null);
  const slots = useMemo(() => ({ leading: leadingSlot, trailing: trailingSlot }), [leadingSlot, trailingSlot]);
  const services = useMemo(() => createEditorServices(), []);
  const userKey = useEditorUserKey();
  const caches = useMemo(() => (open ? createBatchCaches() : null), [open]);
  const brandNames = useMemo(() => groups.map((g) => g.brandName), [groups]);
  const { loading: templatesLoading, getTemplate, getTemplateChoices } = useBrandsTemplates(brandNames, open);

  useEffect(() => {
    if (open) return;
    setChosen({});
    setConfirm(NO_CONFIRM);
    setOffer(null);
    setLastSave(null);
  }, [open]);

  const askConfirm = useCallback((title, message, confirmLabel, onConfirm) => setConfirm({ open: true, title, message, confirmLabel, onConfirm }), []);
  const closeConfirm = useCallback(() => setConfirm((c) => ({ ...c, open: false })), []);

  const activeKey = activeItem?.key;
  // Step 3's Duplicate: a read-only tab that reuses another platform's video.
  const activeIsDuplicate = activeItem?.config?.source === VideoSource.DUPLICATE;
  const activeStatus = activeItem ? statusOf(activeItem) : BatchStatus.IDLE;
  const hasUnsaved = activeStatus === BatchStatus.UNSAVED || activeStatus === BatchStatus.ERROR;
  const markActiveDirty = useCallback(() => activeKey && markUnsaved(activeKey), [activeKey, markUnsaved]);

  // ── Save ───────────────────────────────────────────────────────────────────
  /**
   * Writes the active tab's edit to its card. True when it saved.
   * `finishWhenDone` (the Save button and Ctrl/⌘+S): when that leaves every tab
   * saved, close and go back to Step 3. A save from the leave-this-tab prompt
   * never closes — that prompt carries on with what was asked instead.
   */
  const saveActive = useCallback((finishWhenDone = false) => {
    const item = activeItem;
    if (!item) return false;
    // A tab is finished when it is saved (or a duplicate) AND has its required
    // category — the same two things Step 3 checks before it lets you go on.
    const othersLeft = items.filter((i) => i.key !== item.key && (!isDoneStatus(statusOf(i)) || needsCategory(i))).length;
    const thisNeedsCategory = needsCategory(item);
    if (item.config?.source === VideoSource.DUPLICATE) {
      // Nothing to save: it publishes its source's video.
      if (thisNeedsCategory) {
        toast.warning(`Choose a ${item.platform} category — it is required, duplicates included.`, { toastId: "batch-save" });
        return true;
      }
      if (othersLeft === 0 && finishWhenDone) {
        toast.success("All tabs saved", { toastId: "batch-save" });
        onClose();
        return true;
      }
      toast.info(`${item.platform} duplicates ${item.config.duplicateOf} — nothing to save here.`, { toastId: "batch-save" });
      return true;
    }
    const res = paneRef.current?.getPayload() || { error: "not-ready", message: "Nothing to save yet." };
    if (res.error) {
      // Only an edit that exists and still cannot be saved is an error state.
      if (res.error === "local-media") setMark(item.key, BatchStatus.ERROR);
      setLastSave({ key: item.key, ok: false, text: res.message });
      toast.error(res.message);
      return false;
    }
    // Saving is the review: an applied tab turns green here.
    onConfigChange(item.brandName, item.wgid, item.platform, { source: VideoSource.EDITED, renderPayload: res.payload, batchApplied: null });
    setMark(item.key, null);
    // Only the open tab can carry an unsaved or error mark, so every other tab
    // is either done (saved, or a duplicate) or not edited yet.
    const left = othersLeft;
    if (thisNeedsCategory) {
      const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      setLastSave({ key: item.key, ok: true, text: `Saved ${item.platform} · ${item.wgid} at ${time} — choose a category` });
      toast.warning(`Saved — now choose a ${item.platform} category (required).`, { toastId: "batch-save" });
      return true;
    }
    if (left === 0 && finishWhenDone) {
      toast.success("All tabs saved", { toastId: "batch-save" });
      onClose();
      return true;
    }
    const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    setLastSave({ key: item.key, ok: true, text: `Saved ${item.platform} · ${item.wgid} at ${time}` });
    toast.success(left === 0 ? "Saved — all tabs saved" : `Saved — ${left} tab${left !== 1 ? "s" : ""} left`, { toastId: "batch-save" });
    return true;
  }, [activeItem, items, onClose, onConfigChange, setMark, statusOf]);

  /**
   * Runs `action` (a tab switch, close…) — first asking Save / Don't save /
   * Cancel when the active tab has unsaved edits. A failed save stays put.
   */
  const guard = useCallback(
    (action) => {
      if (!hasUnsaved) {
        action();
        return;
      }
      const item = activeItem;
      setConfirm({
        open: true,
        title: `Save changes to ${item.platform} · ${item.wgid}?`,
        message: "This tab has edits that are not saved yet.",
        confirmLabel: "Save",
        altLabel: "Don't save",
        cancelLabel: "Cancel",
        tone: "primary",
        onConfirm: () => {
          if (saveActive()) action();
        },
        onAlt: () => {
          setMark(item.key, null);
          action();
        },
      });
    },
    [hasUnsaved, activeItem, saveActive, setMark],
  );

  // Ctrl/⌘+S saves the active tab (the editor itself binds no S shortcut).
  // Captured on window so the browser's own "save page" never opens.
  const promptOpen = confirm.open || Boolean(offer);
  const keyState = useRef({});
  keyState.current = { saveActive, promptOpen };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key?.toLowerCase() !== "s") return;
      e.preventDefault();
      e.stopPropagation();
      if (!keyState.current.promptOpen) keyState.current.saveActive(true);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  // ── Navigation (every way out of a tab goes through guard) ────────────────
  const requestClose = () => guard(onClose);
  const selectVideo = (key) => key !== activeItem?.videoKey && guard(() => session.selectVideo(key));
  const selectPlatform = (platform) => platform !== activeItem?.platform && guard(() => session.selectPlatform(platform));

  // A template picked for a tab with no brand template can serve the same
  // platform's other tabs of that brand that have nothing to open either.
  const handleChooseTemplate = (template) => {
    const item = activeItem;
    setChosen((prev) => ({ ...prev, [item.key]: template }));
    const hasBrandTemplate = getTemplate(item.brandName, item.platform, "vertical") || getTemplate(item.brandName, item.platform, "horizontal");
    if (hasBrandTemplate) return;
    const keys = items
      .filter((i) => i.key !== item.key && i.config?.source === VideoSource.EDITED && i.brandName === item.brandName && i.platform === item.platform && !chosen[i.key] && !savedTemplateIdOf(i.config?.renderPayload))
      .map((i) => i.key);
    if (keys.length > 0) setOffer({ template, keys, brandName: item.brandName, platform: item.platform });
  };

  const acceptOffer = () => {
    setChosen((prev) => {
      const next = { ...prev };
      offer.keys.forEach((k) => {
        if (!next[k]) next[k] = offer.template;
      });
      return next;
    });
    setOffer(null);
  };

  const clearSaveNote = useCallback(() => setLastSave(null), []);
  const { removePlatformTab, removeVideoTab } = useTabRemoval({
    items,
    activeItem,
    hasUnsaved,
    onConfigChange,
    onRemoveVideo,
    setMark,
    clearSaveNote,
    askConfirm,
    goTo: session.goTo,
    onClose,
  });
  const handleVideoChoice = useVideoChoice({ activeItem, activeVideo, hasUnsaved, onConfigChange, setMark, clearSaveNote, askConfirm });

  // ── Duplicate… ─────────────────────────────────────────────────────────────
  // The other platforms of this WGID become Step 3's Duplicate of this one.
  // The source must be a SAVED edit first, so it is saved if it is not.
  const applier = useApplyToAll({ items, onConfigChange });
  const handleApplyToAll = async ({ platforms }) => {
    const item = activeItem;
    if (statusOf(item) !== BatchStatus.SAVED && !saveActive(false)) return false;
    const done = applier.run(item, new Set(platforms));
    if (done.length > 0) {
      const names = items.filter((i) => done.includes(i)).map((i) => i.platform).join(", ");
      toast.success(`${names} now duplicate${done.length === 1 ? "s" : ""} ${item.platform} — shown as Duplicate in Step 3.`);
    } else {
      toast.info("Nothing to change.");
    }
    return true;
  };
  const applyToAll = {
    plan: applier.plan,
    needsSave: Boolean(activeItem) && statusOf(activeItem) !== BatchStatus.SAVED,
    onApply: handleApplyToAll,
  };

  /** The open tab's category, written to its Step 3 card. */
  const setCategory = (category) => {
    const item = activeItem;
    onConfigChange(item.brandName, item.wgid, item.platform, { category });
  };

  /** The open tab's Step 3 extra settings (Facebook's media type), written whole. */
  const setExtraConfig = (extra_config) => {
    const item = activeItem;
    onConfigChange(item.brandName, item.wgid, item.platform, { extra_config });
  };

  /** A duplicate tab back to an edit of its own (from the brand template). */
  const editSeparately = () => {
    const item = activeItem;
    onConfigChange(item.brandName, item.wgid, item.platform, { source: VideoSource.EDITED, duplicateOf: null, renderPayload: null });
    toast.info(`${item.platform} now has its own edit — Step 3 shows it as Edited.`);
  };

  const handleCoverChange = useCallback(
    (value) => {
      if (!activeItem) return;
      const extra = activeItem.config?.extra_config || {};
      onConfigChange(activeItem.brandName, activeItem.wgid, activeItem.platform, { extra_config: { ...extra, cover_timestamp: value } });
    },
    [activeItem, onConfigChange],
  );

  /** The Thumbnail Studio's "Capture Thumbnail" save, for the open tab —
   * same conversion PlatformSourceCard uses for Step 3's own capture button. */
  const handleThumbnailChange = useCallback(
    (base64) => {
      const item = activeItem;
      if (!item) return;
      const dataUrl = base64.startsWith("data:image") ? base64 : `data:image/jpeg;base64,${base64}`;
      const arr = dataUrl.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) u8arr[n] = bstr.charCodeAt(n);
      const file = new File([u8arr], `thumbnail_${item.platform.toLowerCase()}.jpg`, { type: mime });
      onConfigChange(item.brandName, item.wgid, item.platform, { thumbnailSource: "custom", thumbnailFile: file, thumbnailPreview: dataUrl });
    },
    [activeItem, onConfigChange],
  );

  const saveNote = lastSave && lastSave.key === activeKey ? lastSave : null;

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      fullScreen
      disableEnforceFocus
      disableEscapeKeyDown
      aria-labelledby="batch-editor-title"
      PaperProps={{ sx: { height: "100vh", bgcolor: "#f8fafc", borderRadius: 0, overflow: "hidden", display: "flex", flexDirection: "column" } }}
    >
      <BatchEditorHeader
        activeItem={activeItem}
        multiBrand={groups.length > 1}
        savedCount={savedCount}
        appliedCount={appliedCount}
        categoryMissingCount={categoryMissingCount}
        total={total}
        onClose={requestClose}
        tabs={
          <PlatformTabBar video={activeVideo} activePlatform={activeItem?.platform} statusOf={statusOf} onSelect={selectPlatform}
            onRemove={removePlatformTab}
            leadingRef={setLeadingSlot}
            trailingRef={setTrailingSlot}
          />
        }
      />

      {/* ── Body: the active tab only ──────────────────────────────────────── */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", px: { xs: 1, md: 1.5 }, pt: 0.5, pb: 0.5 }}>
        {activeItem && activeIsDuplicate ? (
          <DuplicatePane
            item={activeItem}
            onOpenSource={() => session.selectPlatform(activeItem.config.duplicateOf)}
            onEditSeparately={editSeparately}
            onCategoryChange={setCategory}
            onExtraChange={setExtraConfig}
          />
        ) : activeItem && caches ? (
          <BatchEditorPane
            // A new video is a new editor: nothing of the old one carries over.
            key={`${activeItem.key}|${rawIdsOf(activeItem.config).join(",")}`}
            ref={paneRef}
            item={activeItem}
            services={services}
            userKey={userKey}
            caches={caches}
            getTemplate={getTemplate}
            getTemplateChoices={getTemplateChoices}
            templatesLoading={templatesLoading}
            chosenTemplate={chosen[activeItem.key] || null}
            onChooseTemplate={handleChooseTemplate}
            onDirty={markActiveDirty}
            onCoverChange={handleCoverChange}
            askConfirm={askConfirm}
            dirty={hasUnsaved}
            active={!promptOpen}
            siblings={activeVideo ? activeVideo.items.map((i) => ({ platform: i.platform, config: i.config })) : []}
            onVideoChoice={handleVideoChoice}
            slots={slots}
            applyToAll={applyToAll}
            onCategoryChange={setCategory}
            onExtraChange={setExtraConfig}
            onThumbnailChange={handleThumbnailChange}
          />
        ) : (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <Typography variant="body2" color="text.secondary">
              No cards are set to Edited.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Footer: step through the tabs, the video tabs, save the open one ── */}
      <Box sx={{ px: 2, py: 0.5, display: "flex", alignItems: "center", gap: 1, bgcolor: "white", borderTop: "1px solid #e2e8f0", flexShrink: 0 }}>
        <Button size="small" variant="outlined" startIcon={<ChevronLeftIcon />} onClick={() => guard(session.goPrev)} disabled={activeIndex <= 0} sx={outlineSx}>
          Prev
        </Button>
        <Button size="small" variant="outlined" endIcon={<ChevronRightIcon />} onClick={() => guard(session.goNext)} disabled={activeIndex < 0 || activeIndex >= total - 1} sx={outlineSx}>
          Next
        </Button>
        {activeIndex >= 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
            {activeIndex + 1} of {total}
          </Typography>
        )}
        <VideoTabBar
          groups={groups}
          activeVideoKey={activeItem?.videoKey}
          statusOf={statusOf}
          onSelect={selectVideo}
          onRemove={removeVideoTab}
          sx={{ flex: 1, minWidth: 0, ml: 1, border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden", flexShrink: 1 }}
        />
        <Typography
          role="status"
          variant="caption"
          noWrap
          title={saveNote?.text || undefined}
          sx={{ maxWidth: { xs: 120, lg: 260 }, minWidth: 0, flexShrink: 1, fontWeight: 600, color: saveNote ? (saveNote.ok ? "#047857" : "#b91c1c") : "transparent" }}
        >
          {saveNote?.text || ""}
        </Typography>
        <Button size="small" variant="outlined" onClick={requestClose} sx={outlineSx}>
          Close
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={() => saveActive(true)}
          disabled={!activeItem}
          aria-keyshortcuts={IS_MAC ? "Meta+S" : "Control+S"}
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontWeight: 700,
            px: 2.5,
            minHeight: 30,
            py: 0.25,
            fontSize: 13,
            background: "linear-gradient(135deg, #065f46, #10b981)",
            "&:hover": { background: "linear-gradient(135deg, #047857, #059669)" },
          }}
        >
          Save
          <Box component="span" aria-hidden sx={{ ml: 1, px: 0.5, py: 0, borderRadius: 1, fontSize: 10, fontWeight: 600, bgcolor: "rgba(255,255,255,0.2)" }}>
            {SAVE_SHORTCUT}
          </Box>
        </Button>
      </Box>

      <BatchConfirmDialog confirm={confirm} onClose={closeConfirm} />

      <BatchConfirmDialog
        confirm={{
          open: Boolean(offer),
          title: "Use this template for the other tabs?",
          message: offer
            ? `"${offer.template.name}" is now set for this tab. ${offer.brandName} has ${offer.keys.length} other ${offer.platform} tab${offer.keys.length !== 1 ? "s" : ""} with no template — use it there too?`
            : "",
          confirmLabel: offer ? `Use for ${offer.keys.length} more` : "",
          cancelLabel: "Only this tab",
          tone: "primary",
          onConfirm: acceptOffer,
        }}
        onClose={() => setOffer(null)}
      />
    </Dialog>
  );
}
